import type { MaxsoldAuctionDto } from '@jordan/types';

/** Distance for display: metres under 1 km, else whole km. */
export function formatDistance(meters: number | null): string {
  if (meters == null) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

/** A short, local "closes" label from an ISO datetime. */
export function formatClose(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "Toronto, Ontario" / "Toronto" / region fallback. */
export function auctionPlace(a: MaxsoldAuctionDto): string {
  const parts = [a.city, a.province].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return a.region ?? '—';
}

/** Money for display, e.g. "$1,234". null → em dash. */
export function formatMoney(n: number | null): string {
  return n == null ? '—' : `$${n.toLocaleString()}`;
}

/**
 * Time remaining until an auction closes:
 *  - ≥ 1 day  → "4d left"
 *  - < 1 day  → "22h 13m left"
 *  - ≤ 0      → "Ended"
 * `urgent` is true when under 6 hours remain (caller styles it bold red).
 */
export function formatTimeLeft(closeIso: string, now: number): { text: string; urgent: boolean; ended: boolean } {
  const end = Date.parse(closeIso);
  if (!Number.isFinite(end)) return { text: '—', urgent: false, ended: false };
  const ms = end - now;
  if (ms <= 0) return { text: 'Ended', urgent: false, ended: true };

  const HOUR = 3_600_000;
  const DAY = 24 * HOUR;
  const urgent = ms < 6 * HOUR;
  if (ms >= DAY) return { text: `${Math.floor(ms / DAY)}d left`, urgent, ended: false };

  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { text: `${hours}h ${minutes}m left`, urgent, ended: false };
}

/** Escape text for safe interpolation into InfoWindow HTML. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
